// Spec 084 — a batelada: características efetivas, origem e correção.
//
// A batelada é a execução de produção. Ela carrega valores PRÓPRIOS e bateladas de ORIGEM, das
// quais herda o que não declara. A herança é derivada a cada leitura, então esta resposta nunca é
// uma cópia: corrigir a origem muda o que a herdeira mostra na próxima leitura (FR-010).
import { z } from "zod";
import { UuidSchema } from "./common.js";
import { BatchCharacteristicKindSchema } from "./batch-characteristics.js";
import { BATCH_CODE_PATTERN } from "../lib/batch-code.js";

/** Um valor informado: da lista (`valueId`) ou texto livre (`text`), nunca os dois. */
export const CharacteristicValueInputSchema = z
  .object({
    characteristicId: UuidSchema,
    valueId: UuidSchema.optional(),
    text: z.string().trim().min(1).max(200).optional(),
  })
  .strict()
  .refine((v) => (v.valueId === undefined) !== (v.text === undefined), {
    message: "Informe um valor da lista ou um texto, não os dois.",
  });
export type CharacteristicValueInput = z.infer<typeof CharacteristicValueInputSchema>;

/** Na correção, `clear` volta a característica para "não informado". */
export const CharacteristicValueClearSchema = z
  .object({ characteristicId: UuidSchema, clear: z.literal(true) })
  .strict();

export const CharacteristicValuesInputSchema = z
  .array(CharacteristicValueInputSchema)
  .max(50)
  .refine((vs) => new Set(vs.map((v) => v.characteristicId)).size === vs.length, {
    message: "Cada característica aparece uma vez só.",
  });

export const RunOriginStatusSchema = z.enum(["PENDING", "CONFIRMED", "NONE"]);
export type RunOriginStatus = z.infer<typeof RunOriginStatusSchema>;

export const BatchValueSourceSchema = z.enum(["SCREEN", "ORDER", "IMPORT"]);
export type BatchValueSource = z.infer<typeof BatchValueSourceSchema>;

export const EffectiveCharacteristicStateSchema = z.enum(["OWN", "INHERITED", "MIXED", "NONE"]);
export type EffectiveCharacteristicState = z.infer<typeof EffectiveCharacteristicStateSchema>;

export const EffectiveCharacteristicSchema = z.object({
  characteristicId: UuidSchema,
  name: z.string(),
  kind: BatchCharacteristicKindSchema,
  required: z.boolean(),
  state: EffectiveCharacteristicStateSchema,
  /** `MIXED` ⇒ dois ou mais; `NONE` ⇒ vazio. */
  values: z.array(z.object({ valueId: UuidSchema.nullable(), label: z.string() })),
  /** Só em `OWN`. */
  source: BatchValueSourceSchema.nullable(),
  /** Em `INHERITED` e `MIXED`: as origens diretas da batelada, por rótulo. */
  inheritedFrom: z.array(z.object({ runId: UuidSchema.nullable(), label: z.string() })),
  /** FR-010 (herdada não se edita) × FR-012 (estado × papel). */
  editable: z.boolean(),
  /** Obrigatória numa linha de marcação e sem valor. */
  missingRequired: z.boolean(),
});
export type EffectiveCharacteristic = z.infer<typeof EffectiveCharacteristicSchema>;

export const BatchOriginSchema = z.object({
  /** Nulo = a batelada de origem foi desfeita junto com a importação que a criou. */
  runId: UuidSchema.nullable(),
  label: z.string(),
  source: z.enum(["SCREEN", "IMPORT"]),
});
export type BatchOrigin = z.infer<typeof BatchOriginSchema>;

export const OriginSuggestionSchema = z.object({
  upstreamLineId: UuidSchema,
  upstreamLineName: z.string(),
  runId: UuidSchema,
  batchCode: z.string(),
  startedAt: z.string(),
});
export type OriginSuggestion = z.infer<typeof OriginSuggestionSchema>;

export const BatchResponseSchema = z.object({
  runId: UuidSchema,
  lineId: UuidSchema,
  lineName: z.string(),
  batchCode: z.string(),
  status: z.enum(["ACTIVE", "PAUSED", "COMPLETED"]),
  startedAt: z.string(),
  completedAt: z.string().nullable(),
  originStatus: RunOriginStatusSchema.nullable(),
  origins: z.array(BatchOriginSchema),
  /** Vazio quando a linha não tem montante ou nenhuma execução a montante começou antes. */
  suggestion: z.array(OriginSuggestionSchema),
  characteristics: z.array(EffectiveCharacteristicSchema),
  canEditCharacteristics: z.boolean(),
  canEditOrigins: z.boolean(),
  canEditBatchCode: z.boolean(),
});
export type BatchResponse = z.infer<typeof BatchResponseSchema>;

export const BatchListQuerySchema = z
  .object({
    limit: z.coerce.number().int().min(1).max(50).default(20),
    cursor: UuidSchema.optional(),
  })
  .strict();
export type BatchListQuery = z.infer<typeof BatchListQuerySchema>;

export const BatchListItemSchema = z.object({
  runId: UuidSchema,
  batchCode: z.string(),
  status: z.enum(["ACTIVE", "PAUSED", "COMPLETED"]),
  startedAt: z.string(),
  completedAt: z.string().nullable(),
  originStatus: RunOriginStatusSchema.nullable(),
  characteristics: z.array(
    z.object({
      name: z.string(),
      state: EffectiveCharacteristicStateSchema,
      labels: z.array(z.string()),
      missingRequired: z.boolean(),
    })
  ),
});
export type BatchListItem = z.infer<typeof BatchListItemSchema>;

export const BatchListResponseSchema = z.object({
  items: z.array(BatchListItemSchema),
  nextCursor: UuidSchema.nullable(),
  /** Bateladas dos últimos 30 dias com origem pendente e com obrigatória sem valor. */
  pendingOrigins: z.number().int(),
  missingRequired: z.number().int(),
});
export type BatchListResponse = z.infer<typeof BatchListResponseSchema>;

export const SetBatchCharacteristicsRequestSchema = z
  .object({
    values: z
      .array(z.union([CharacteristicValueInputSchema, CharacteristicValueClearSchema]))
      .min(1)
      .max(50),
  })
  .strict()
  // A mesma regra do agendamento: uma característica aparece uma vez só. As correções são aplicadas
  // em ordem, então duas entradas para a mesma característica fazem a última vencer em silêncio — e
  // `{valueId}` seguido de `{clear}` apagaria justamente o valor que o mesmo pedido acabou de pedir.
  .refine((b) => new Set(b.values.map((v) => v.characteristicId)).size === b.values.length, {
    message: "Cada característica aparece uma vez só.",
    path: ["values"],
  });
export type SetBatchCharacteristicsRequest = z.infer<typeof SetBatchCharacteristicsRequestSchema>;

export const SetRunOriginsRequestSchema = z
  .object({ originRunIds: z.array(UuidSchema).max(20) })
  .strict()
  .refine((b) => new Set(b.originRunIds).size === b.originRunIds.length, {
    message: "A mesma origem aparece mais de uma vez.",
  });
export type SetRunOriginsRequest = z.infer<typeof SetRunOriginsRequestSchema>;

export const SetBatchCodeRequestSchema = z
  .object({
    batchCode: z
      .string()
      .trim()
      .regex(BATCH_CODE_PATTERN, "Use até 40 letras, números, ponto, hífen ou sublinhado."),
  })
  .strict();
export type SetBatchCodeRequest = z.infer<typeof SetBatchCodeRequestSchema>;
